import { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface EnhancedStatCardProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  trend?: {
    value: number;
    label: string;
    isPositive?: boolean;
  };
  className?: string;
  colorScheme?: 'primary' | 'success' | 'warning' | 'neutral';
}

const colorSchemes = {
  primary: {
    iconBg: "bg-primary/10 group-hover:bg-primary/20 border-primary/20",
    iconColor: "text-primary group-hover:text-primary-hover",
    gradient: "from-primary/10 to-primary/20"
  },
  success: {
    iconBg: "bg-emerald-50 group-hover:bg-emerald-100/80 border-emerald-100",
    iconColor: "text-emerald-600 group-hover:text-emerald-700",
    gradient: "from-emerald-50/40 to-emerald-100/20"
  },
  warning: {
    iconBg: "bg-amber-50 group-hover:bg-amber-100/80 border-amber-100",
    iconColor: "text-amber-600 group-hover:text-amber-700",
    gradient: "from-amber-50/40 to-amber-100/20"
  },
  neutral: {
    iconBg: "bg-muted group-hover:bg-muted/80 border-border",
    iconColor: "text-muted-foreground group-hover:text-foreground",
    gradient: "from-muted/40 to-muted/20"
  }
};

export function EnhancedStatCard({
  title,
  value,
  icon: Icon,
  trend,
  className = "",
  colorScheme = 'primary'
}: EnhancedStatCardProps) {
  const colors = colorSchemes[colorScheme];

  return (
    <div className={cn(
      "relative group bg-card border border-border hover:border-border/80 rounded-2xl p-6",
      "shadow-sm hover:shadow-lg transition-all duration-300 ease-out",
      "hover:-translate-y-0.5 cursor-default overflow-hidden",
      className
    )}>
      {/* Subtle gradient overlay */}
      <div className={cn(
        "absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-300",
        colors.gradient
      )} />

      {/* Content */}
      <div className="relative z-10">
        {/* Icon */}
        <div className="flex items-center justify-between mb-4">
          <div className={cn(
            "w-12 h-12 rounded-xl border flex items-center justify-center transition-all duration-300 shadow-sm",
            colors.iconBg
          )}>
            <Icon className={cn("h-6 w-6 transition-colors duration-300", colors.iconColor)} />
          </div>

          {/* Trend indicator */}
          {trend && (
            <div className={cn(
              "px-2 py-1 rounded-full text-xs font-medium border backdrop-blur-sm",
              trend.isPositive !== false
                ? "bg-emerald-50/80 text-emerald-700 border-emerald-200/60"
                : "bg-red-50/80 text-red-700 border-red-200/60"
            )}>
              {trend.isPositive !== false ? '+' : ''}{trend.value}% {trend.label}
            </div>
          )}
        </div>

        {/* Value */}
        <div className="mb-2">
          <div className="text-3xl font-bold text-foreground tabular-nums leading-none">
            {value}
          </div>
        </div>

        {/* Title */}
        <div className="text-sm font-medium text-muted-foreground leading-tight">
          {title}
        </div>
      </div>

      {/* Hover accent */}
      <div className={cn(
        "absolute bottom-0 left-0 right-0 h-1 rounded-b-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300",
        colorScheme === 'primary' && "bg-gradient-to-r from-primary to-primary/80",
        colorScheme === 'success' && "bg-gradient-to-r from-emerald-400 to-emerald-500",
        colorScheme === 'warning' && "bg-gradient-to-r from-amber-400 to-amber-500",
        colorScheme === 'neutral' && "bg-gradient-to-r from-gray-400 to-gray-500"
      )} />
    </div>
  );
}
